import React, { useCallback, useEffect, useMemo, useState } from "react";
import AuthService from "../../../api/services/AuthService";
import {
    computeInitialMarginLevel,
    formatMarginLevel,
    formatThresholdLevel,
    getMarginLevelStatus,
    parseMarginLevel,
    resolveMarginThresholds,
    pairHasDebt,
    parseEffectiveMultiple,
} from "./marginLevelUtils";

function RiskMeter({ ml, liqLevel, callLevel, hasDebt }) {
    if (liqLevel == null || callLevel == null) return null;

    const min = Math.max(0.5, liqLevel - 0.08);
    const max = Math.max(callLevel + 0.12, liqLevel + 0.25);
    const range = max - min || 1;
    const liqPct = Math.min(100, Math.max(0, ((liqLevel - min) / range) * 100));
    const callPct = Math.min(100, Math.max(liqPct, ((callLevel - min) / range) * 100));
    const markerPct = hasDebt && ml != null
        ? Math.min(100, Math.max(0, ((ml - min) / range) * 100))
        : null;

    return (
        <div className="agce-margin-risk-meter">
            <div className="agce-margin-risk-meter__track">
                <div
                    className="agce-margin-risk-meter__zone agce-margin-risk-meter__zone--danger"
                    style={{ width: `${liqPct}%` }}
                />
                <div
                    className="agce-margin-risk-meter__zone agce-margin-risk-meter__zone--warning"
                    style={{ width: `${callPct - liqPct}%` }}
                />
                <div className="agce-margin-risk-meter__zone agce-margin-risk-meter__zone--normal" />
                {markerPct != null ? (
                    <div className="agce-margin-risk-meter__marker" style={{ left: `${markerPct}%` }} aria-hidden />
                ) : null}
            </div>
            <div className="agce-margin-risk-meter__labels">
                <span className="agce-margin-risk-meter__label" style={{ left: `${liqPct}%` }}>
                    {formatThresholdLevel(liqLevel)}
                </span>
                <span className="agce-margin-risk-meter__label" style={{ left: `${callPct}%` }}>
                    {formatThresholdLevel(callLevel)}
                </span>
            </div>
        </div>
    );
}

function RiskTierRow({ tone, title, subtitle, active }) {
    return (
        <div className={`agce-margin-risk-tier${active ? " agce-margin-risk-tier--active" : ""}`}>
            <span className={`agce-margin-risk-tier__dot agce-margin-risk-tier__dot--${tone}`} aria-hidden />
            <div className="agce-margin-risk-tier__content">
                <div className="agce-margin-risk-tier__title">{title}</div>
                {subtitle ? <p className="agce-margin-risk-tier__subtitle">{subtitle}</p> : null}
            </div>
        </div>
    );
}

function CollapsibleSection({ title, open, onToggle, children }) {
    return (
        <section className={`agce-margin-risk-section${open ? " agce-margin-risk-section--open" : ""}`}>
            <button type="button" className="agce-margin-risk-section__header" onClick={onToggle} aria-expanded={open}>
                <span>{title}</span>
                <i className={open ? "ri-subtract-line" : "ri-add-line"} aria-hidden />
            </button>
            {open ? <div className="agce-margin-risk-section__body">{children}</div> : null}
        </section>
    );
}

function StatusRule({ allowed, label, value }) {
    return (
        <div className="agce-margin-risk-rule">
            <span className={`agce-margin-risk-rule__dot${allowed ? " agce-margin-risk-rule__dot--ok" : ""}`} aria-hidden />
            <span className="agce-margin-risk-rule__label">{label}</span>
            <span className="agce-margin-risk-rule__value">{value}</span>
        </div>
    );
}

export default function IsolatedMarginRiskModal({ row, onClose }) {
    const [liveAccount, setLiveAccount] = useState(null);
    const [borrowOpen, setBorrowOpen] = useState(false);
    const [transferOpen, setTransferOpen] = useState(false);

    const fetchLive = useCallback(() => {
        if (!row?.pair_id) return;
        AuthService.marginAccount(row.pair_id)
            .then((res) => { if (res?.success) setLiveAccount(res.data); })
            .catch(() => {});
    }, [row?.pair_id]);

    useEffect(() => { fetchLive(); }, [fetchLive]);

    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [onClose]);

    const account = useMemo(() => ({
        ...row,
        ...liveAccount,
        margin_call_level: liveAccount?.margin_call_level ?? row?.margin_call_level ?? row?.marginCallLevel,
        liquidation_margin_level: liveAccount?.liquidation_margin_level ?? row?.liquidation_margin_level ?? row?.liquidationMarginLevel,
        leverage: liveAccount?.leverage ?? liveAccount?.default_leverage ?? row?.leverage ?? row?.default_leverage,
        effective_multiple: liveAccount?.effective_multiple ?? liveAccount?.effective_leverage ?? row?.effective_multiple ?? row?.effective_leverage ?? row?.effectiveMultiple,
        margin_level: liveAccount?.margin_level ?? row?.margin_level ?? row?.marginLevel,
    }), [row, liveAccount]);

    const leverage = parseMarginLevel(account.leverage ?? account.default_leverage ?? account.max_leverage);
    const effectiveMultiple = parseEffectiveMultiple(account);
    const thresholds = resolveMarginThresholds(account);
    const hasDebt = row?.hasDebt ?? pairHasDebt(account);
    const ml = hasDebt ? parseMarginLevel(account.margin_level) : null;
    const status = getMarginLevelStatus(ml, thresholds, { hasDebt });
    const initialMl = computeInitialMarginLevel(effectiveMultiple ?? leverage);
    const callFmt = formatThresholdLevel(thresholds.marginCallLevel);
    const liqFmt = formatThresholdLevel(thresholds.liquidationMarginLevel);
    const thresholdsReady = thresholds.marginCallLevel != null && thresholds.liquidationMarginLevel != null;
    const pairDisplay = String(account.pairRaw || account.pair || "—").replace("/", "");
    const effectiveMultipleLabel = effectiveMultiple ? `${effectiveMultiple}x` : null;
    const statusPillClass = `agce-margin-risk-hero__pill agce-margin-risk-hero__pill--${status.key}`;

    return (
        <>
            <div className="modal-backdrop fade show" onClick={onClose} aria-hidden />
            <div
                className="modal fade kyc_modal margin-modal-container agce-margin-risk-modal show d-block"
                tabIndex="-1"
                role="dialog"
                aria-modal="true"
                style={{ zIndex: 1055 }}
            >
                <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable agce-margin-risk-modal__dialog">
                    <div className="modal-content agce-margin-risk-modal__content">
                        <div className="modal-header agce-margin-risk-modal__header">
                            <div>
                                <h5 className="agce-margin-risk-modal__title">Margin risk</h5>
                                <p className="agce-margin-risk-modal__subtitle">
                                    Isolated position, {pairDisplay}
                                    {effectiveMultipleLabel ? ` · Effective multiple ${effectiveMultipleLabel}` : ""}
                                </p>
                            </div>
                            <button type="button" className="btn-close agce-margin-risk-modal__close" aria-label="Close" onClick={onClose} />
                        </div>

                        <div className="modal-body agce-margin-risk-modal__body">
                            <section className="agce-margin-risk-hero">
                                <div className="agce-margin-risk-hero__top">
                                    {hasDebt && ml != null ? (
                                        <>
                                            <span className="agce-margin-risk-hero__value">{formatMarginLevel(ml)}</span>
                                            {thresholdsReady ? (
                                                <span className={statusPillClass}>{status.label}</span>
                                            ) : (
                                                <span className="agce-margin-risk-hero__pill agce-margin-risk-hero__pill--unavailable">Loading…</span>
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            <span className="agce-margin-risk-hero__value agce-margin-risk-hero__value--safe">Safe</span>
                                            <span className="agce-margin-risk-hero__pill agce-margin-risk-hero__pill--safe">No debt</span>
                                        </>
                                    )}
                                </div>
                                <p className="agce-margin-risk-hero__formula">
                                    Margin level = asset value ÷ (debt + unpaid interest), at mark price
                                </p>
                            </section>

                            {thresholdsReady ? (
                                <>
                                    <RiskMeter
                                        ml={ml}
                                        liqLevel={thresholds.liquidationMarginLevel}
                                        callLevel={thresholds.marginCallLevel}
                                        hasDebt={hasDebt}
                                    />

                                    <div className="agce-margin-risk-tier-list">
                                        <RiskTierRow
                                            tone="normal"
                                            title={`Normal · above ${callFmt}`}
                                            subtitle="Borrowing and transfers out are allowed if the level stays above the margin call level afterward."
                                            active={status.key === "normal"}
                                        />
                                        <RiskTierRow
                                            tone="warning"
                                            title={`Margin call · ${liqFmt} to ${callFmt}`}
                                            subtitle="Add collateral or repay debt. New borrowing and transfers out are blocked; trading still works."
                                            active={status.key === "margin_call"}
                                        />
                                        <RiskTierRow
                                            tone="danger"
                                            title={`Liquidated · ${liqFmt} or below`}
                                            subtitle="Open orders are cancelled and the position closes at market. A 2% liquidation fee applies."
                                            active={status.key === "liquidated"}
                                        />
                                    </div>
                                </>
                            ) : (
                                <p className="agce-margin-risk-modal__loading">Loading risk thresholds…</p>
                            )}

                            {initialMl != null && (effectiveMultiple || leverage) ? (
                                <p className="agce-margin-risk-modal__footnote">
                                    At full {effectiveMultipleLabel || `${leverage}x`} with no extra collateral, the starting margin level is {formatMarginLevel(initialMl)}.
                                </p>
                            ) : null}

                            <div className="agce-margin-risk-modal__sections">
                                <CollapsibleSection
                                    title="Borrow margin level"
                                    open={borrowOpen}
                                    onToggle={() => setBorrowOpen((v) => !v)}
                                >
                                    <p className="agce-margin-risk-section__desc">
                                        Uses the same margin call level as the risk bands above.
                                    </p>
                                    {thresholdsReady ? (
                                        <div className="agce-margin-risk-rules">
                                            <StatusRule allowed label="Borrowable" value={`ML > ${callFmt}`} />
                                            <StatusRule label="Not borrowable" value={`ML ≤ ${callFmt}`} />
                                        </div>
                                    ) : null}
                                </CollapsibleSection>

                                <CollapsibleSection
                                    title="Transfer out"
                                    open={transferOpen}
                                    onToggle={() => setTransferOpen((v) => !v)}
                                >
                                    <p className="agce-margin-risk-section__desc">
                                        Transfer-out requires ML above the margin call level before and after the transfer.
                                    </p>
                                    {thresholdsReady ? (
                                        <div className="agce-margin-risk-rules">
                                            <StatusRule allowed label="Permitted" value={`ML > ${callFmt}`} />
                                            <StatusRule label="Not permitted" value={`ML ≤ ${callFmt}`} />
                                        </div>
                                    ) : null}
                                </CollapsibleSection>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
